import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const SimpleAuthContext = createContext();

// Dynamic backend URL detection
const getBackendUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '';
  }
  
  if (hostname === 'prospermind.com') {
    return `https://prospermind.com`;
  }
  
  if (hostname.includes('emergent.host') || hostname.includes('emergentagent.com')) {
    return `https://${hostname}`;
  }
  
  return `https://${hostname}`;
};

export const useAuth = () => {
  const context = useContext(SimpleAuthContext);
  if (!context) {
    throw new Error('useAuth must be used within a SimpleAuthProvider');
  }
  return context;
};

export const SimpleAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const checkAuthAndCallback = async () => {
      const backendUrl = getBackendUrl();
      
      // Check for OAuth callback with session_id in hash (Emergent Google Auth)
      const hash = window.location.hash;
      console.log('[Auth] Page loaded. Hash:', hash, 'Path:', window.location.pathname);
      
      if (hash && hash.includes('session_id=')) {
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        if (sessionIdMatch) {
          const sessionId = sessionIdMatch[1];
          console.log('[Auth] OAuth callback detected. Exchanging session_id...');
          
          try {
            const response = await fetch(`${backendUrl}/api/auth/session`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ session_id: sessionId }),
            });

            console.log('[Auth] Session exchange response:', response.status);
            
            if (response.ok) {
              const data = await response.json();
              console.log('[Auth] Session exchange SUCCESS');
              
              const token = data.session_token;
              const userData = {
                id: data.user_id,
                name: data.name || data.email?.split('@')[0],
                email: data.email,
                picture: data.picture,
                subscription_plan: data.subscription_tier || 'free',
                auth_type: 'oauth'
              };
              
              // Store auth data
              sessionStorage.setItem('prosperMindAuth', 'true');
              sessionStorage.setItem('prosperMindUser', JSON.stringify(userData));
              sessionStorage.setItem('prosperMindToken', token);
              sessionStorage.setItem('authToken', token);
              sessionStorage.setItem('session_token', token);
              axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
              
              // Save pre-login quiz if exists
              const preLoginQuizResult = sessionStorage.getItem('preLoginQuizResult');
              if (preLoginQuizResult) {
                try {
                  const quizData = JSON.parse(preLoginQuizResult);
                  await fetch(`${backendUrl}/api/quick-quiz/submit`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(quizData)
                  });
                  sessionStorage.removeItem('preLoginQuizResult');
                } catch (e) {
                  console.error('[Auth] Quiz save error:', e);
                }
              }
              
              // REDIRECT TO DASHBOARD
              console.log('[Auth] Redirecting to /dashboard');
              window.location.href = '/dashboard';
              return;
            } else {
              console.error('[Auth] Session exchange FAILED:', response.status);
            }
          } catch (error) {
            console.error('[Auth] Session exchange ERROR:', error);
          }
          
          // Clean URL on failure
          window.history.replaceState({}, document.title, '/');
        }
      }
      
      // Check for token in query string (alternative OAuth flow)
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      
      if (tokenFromUrl) {
        console.log('[Auth] Token found in URL, verifying...');
        
        try {
          const response = await fetch(`${backendUrl}/api/auth/google/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: tokenFromUrl }),
          });

          if (response.ok) {
            const data = await response.json();
            
            const userData = {
              id: data.user?.id || data.user_id,
              name: data.user?.name || data.name || data.email?.split('@')[0],
              email: data.user?.email || data.email,
              picture: data.user?.picture,
              subscription_plan: data.user?.subscription_tier || 'free',
              auth_type: 'oauth'
            };
            
            sessionStorage.setItem('prosperMindAuth', 'true');
            sessionStorage.setItem('prosperMindUser', JSON.stringify(userData));
            sessionStorage.setItem('prosperMindToken', tokenFromUrl);
            sessionStorage.setItem('authToken', tokenFromUrl);
            sessionStorage.setItem('session_token', tokenFromUrl);
            axios.defaults.headers.common['Authorization'] = `Bearer ${tokenFromUrl}`;
            
            console.log('[Auth] Token verified, redirecting to /dashboard');
            window.location.href = '/dashboard';
            return;
          }
        } catch (error) {
          console.error('[Auth] Token verify error:', error);
        }
        
        window.history.replaceState({}, document.title, '/');
      }
      
      // Check for existing session in storage
      const storedAuth = sessionStorage.getItem('prosperMindAuth');
      const storedUser = sessionStorage.getItem('prosperMindUser');
      const storedToken = sessionStorage.getItem('prosperMindToken');
      
      if (storedAuth === 'true' && storedUser && storedToken) {
        console.log('[Auth] Found existing session in storage');
        setIsAuthenticated(true);
        setUser(JSON.parse(storedUser));
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        sessionStorage.setItem('authToken', storedToken);
      }
      
      setIsLoading(false);
    };
    
    checkAuthAndCallback();
  }, []);

  const login = async (username, password) => {
    const HARDCODED_USERS = {
      'aelgqvist': {
        username: 'aelgqvist',
        password: 'aelgqvist',
        id: 'user_aelgqvist',
        name: 'A Elgqvist',
        email: 'aelgqvist@prospermind.com',
        subscription_plan: 'premium'
      },
      'aelgqvist1': {
        username: 'aelgqvist1',
        password: 'aelgqvist1',
        id: 'user_aelgqvist1',
        name: 'A Elgqvist (Free)',
        email: 'aelgqvist1@prospermind.com',
        subscription_plan: 'free'
      },
      'testfree': {
        username: 'testfree',
        password: 'testfree',
        id: 'user_testfree',
        name: 'Test Free User',
        email: 'testfree@prospermind.com',
        subscription_plan: 'free'
      }
    };

    const HARDCODED_USER = HARDCODED_USERS[username];

    if (HARDCODED_USER && password === HARDCODED_USER.password) {
      const userData = {
        id: HARDCODED_USER.id,
        username: HARDCODED_USER.username,
        name: HARDCODED_USER.name,
        email: HARDCODED_USER.email,
        subscription_plan: HARDCODED_USER.subscription_plan
      };
      
      const token = `hardcoded_token_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      sessionStorage.setItem('prosperMindAuth', 'true');
      sessionStorage.setItem('prosperMindUser', JSON.stringify(userData));
      sessionStorage.setItem('prosperMindToken', token);
      sessionStorage.setItem('authToken', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setIsAuthenticated(true);
      setUser(userData);
      
      // Also try backend session
      try {
        const backendUrl = getBackendUrl();
        const response = await fetch(`${backendUrl}/api/auth/simple/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.token) {
            sessionStorage.setItem('prosperMindToken', data.token);
            sessionStorage.setItem('authToken', data.token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
            
            if (data.user) {
              const updatedUserData = { ...userData, ...data.user };
              sessionStorage.setItem('prosperMindUser', JSON.stringify(updatedUserData));
              setUser(updatedUserData);
            }
          }
        }
      } catch (e) {
        console.log('Backend session creation failed:', e.message);
      }
      
      return { success: true };
    }

    // Try backend login
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/auth/simple/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const userData = data.user;
        const token = data.token;
        
        sessionStorage.setItem('prosperMindAuth', 'true');
        sessionStorage.setItem('prosperMindUser', JSON.stringify(userData));
        sessionStorage.setItem('prosperMindToken', token);
        sessionStorage.setItem('authToken', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        setIsAuthenticated(true);
        setUser(userData);
        return { success: true };
      } else {
        return { success: false, error: data.detail || data.error || 'Invalid username or password' };
      }
    } catch (error) {
      return { success: false, error: 'Invalid username or password' };
    }
  };

  const logout = () => {
    sessionStorage.removeItem('prosperMindAuth');
    sessionStorage.removeItem('prosperMindUser');
    sessionStorage.removeItem('prosperMindToken');
    sessionStorage.removeItem('authToken');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setUser(null);
  };

  const getAccessToken = () => {
    return sessionStorage.getItem('prosperMindToken');
  };

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    getAccessToken,
    loginWithRedirect: () => console.log('Redirect to login form'),
    error: null
  };

  return (
    <SimpleAuthContext.Provider value={value}>
      {children}
    </SimpleAuthContext.Provider>
  );
};

export default SimpleAuthProvider;
